import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import AbstractListView from '@UI/Store/AbstractListView';
import type { ImportedDocumentsPayload } from '@Application/Beneficiaries/ImportedDocuments/Actions/importedDocumentsFetched';
import ImportedDocumentView from '@UI/Store/ImportedDocuments/Views/ImportedDocumentView';

/**
 * Immutable View: `readonly` properties, frozen at the end of the constructor.
 * `typeKey` and `subjectId` scope this list: the store compares them (`isFor`) against the dossier on
 * screen.
 */
class ImportedDocumentsListView extends AbstractListView<ImportedDocumentView> {
	readonly typeKey: string;
	readonly subjectId: number;
	readonly totalRecords: number;

	constructor(
		list: readonly ImportedDocumentView[],
		typeKey: string,
		subjectId: number,
		totalRecords: number,
	) {
		super(list, 'ImportedDocumentsListView');

		this.typeKey = typeKey;
		this.subjectId = subjectId;
		this.totalRecords = totalRecords;

		Object.freeze(this);
	}

	isFor(subject: SubjectAddress): boolean {
		return this.typeKey === subject.typeKey && this.subjectId === subject.subjectId;
	}

	/** At least one document is still being converted. */
	get hasConverting(): boolean {
		return this.list.some((importedDocument) => importedDocument.isConverting);
	}

	/** Returns a new list with `view` first; the same instance when a row already carries its `id`. */
	addImportedDocument(view: ImportedDocumentView): ImportedDocumentsListView {
		if (this.findById(view.id) !== null) {
			return this;
		}

		return new ImportedDocumentsListView(
			[view, ...this.list],
			this.typeKey,
			this.subjectId,
			this.totalRecords + 1,
		);
	}

	/** Returns a new list where the row of the same `id` is replaced; the same instance when none is held. */
	replaceImportedDocument(view: ImportedDocumentView): ImportedDocumentsListView {
		if (this.findById(view.id) === null) {
			return this;
		}

		return new ImportedDocumentsListView(
			this.list.map((importedDocument) => (importedDocument.id === view.id ? view : importedDocument)),
			this.typeKey,
			this.subjectId,
			this.totalRecords,
		);
	}

	static fromPayload(
		payload: ImportedDocumentsPayload,
		typeKey: string,
		subjectId: number,
	): ImportedDocumentsListView {
		return new ImportedDocumentsListView(
			payload.results.map((importedDocument) => new ImportedDocumentView(importedDocument)),
			typeKey,
			subjectId,
			payload.totalRecords,
		);
	}
}

export default ImportedDocumentsListView;
